import { useRouteError, isRouteErrorResponse, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const isResponse = isRouteErrorResponse(error);
  const message = isResponse
    ? `${error.status} ${error.statusText}`
    : (error as Error)?.message || String(error);
  const stack = !isResponse ? (error as Error)?.stack : undefined;
  const route = location.pathname + location.search;

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.error('[RouteError]', { route, message, error });
  }, [route, message, error]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-bg-primary">
      <div className="ui-card max-w-lg w-full p-6 2xl:p-8 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-danger/10 text-danger">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <h1 className="text-lg 2xl:text-xl font-semibold text-text-primary mb-2">
          {t('errors.somethingWrong', 'حدث خطأ غير متوقع')}
        </h1>
        <p className="text-sm text-text-muted mb-1">
          {t('errors.route', 'الصفحة')}: <code className="mono" dir="ltr">{route}</code>
        </p>
        <p className="text-sm text-danger mb-5 break-words" dir="ltr">{message}</p>

        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => window.location.reload()}
            className="ui-btn-primary inline-flex items-center gap-1.5"
          >
            <RefreshCw className="h-4 w-4" />
            {t('common.reload', 'إعادة تحميل')}
          </button>
          <button
            onClick={() => navigate('/')}
            className="ui-btn-secondary inline-flex items-center gap-1.5"
          >
            <Home className="h-4 w-4" />
            {t('sidebar.home', 'الرئيسية')}
          </button>
        </div>

        {import.meta.env.DEV && stack && (
          <pre className="mt-5 max-h-60 overflow-auto rounded-lg bg-bg-tertiary p-3 text-[11px] text-start text-text-muted" dir="ltr">
            {stack}
          </pre>
        )}
      </div>
    </div>
  );
}
